import React, { useState, useEffect, useMemo } from 'react';
import {
  Package, Search, AlertTriangle, XCircle, ChevronDown, ChevronRight,
  Edit3, Save, X, Plus, Trash2, ExternalLink, Eye, EyeOff,
  Box, ShieldCheck, RefreshCw
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_COLORS = {
  tool: { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200', dot: 'bg-blue-500' },
  toy: { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', dot: 'bg-amber-500' },
  swag: { bg: 'bg-purple-50', text: 'text-purple-700', border: 'border-purple-200', dot: 'bg-purple-500' },
};

const PHASE_MAP = {
  1: 'Foundation', 2: 'Foundation', 3: 'Foundation',
  4: 'Application', 5: 'Application', 6: 'Application', 7: 'Application',
  8: 'Transformation', 9: 'Transformation', 10: 'Transformation',
};

const PHASE_COLORS = {
  'Foundation': 'from-emerald-500 to-teal-600',
  'Application': 'from-blue-500 to-indigo-600',
  'Transformation': 'from-orange-500 to-red-600',
};

function StockBadge({ quantity, threshold }) {
  if (quantity === 0) {
    return (
      <span data-testid="stock-badge-out" className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold bg-red-100 text-red-700 border border-red-200">
        <XCircle className="w-3 h-3" /> Out of Stock
      </span>
    );
  }
  if (quantity <= threshold) {
    return (
      <span data-testid="stock-badge-low" className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold bg-amber-100 text-amber-700 border border-amber-200">
        <AlertTriangle className="w-3 h-3" /> Low Stock
      </span>
    );
  }
  return (
    <span data-testid="stock-badge-ok" className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold bg-green-100 text-green-700 border border-green-200">
      <ShieldCheck className="w-3 h-3" /> In Stock
    </span>
  );
}

function ItemEditModal({ item, open, onClose, onSave }) {
  const [form, setForm] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (item) {
      setForm({
        quantity_on_hand: item.quantity_on_hand ?? 0,
        low_stock_threshold: item.low_stock_threshold ?? 5,
        supplier_name: item.supplier_name ?? '',
        supplier_url: item.supplier_url ?? '',
        supplier_username: item.supplier_username ?? '',
        supplier_password: item.supplier_password ?? '',
        notes: item.notes ?? '',
      });
      setShowPassword(false);
    }
  }, [item]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/admin/inventory/${item.id}`, form);
      onSave(res.data);
      toast.success(`Updated ${item.item_name}`);
      onClose();
    } catch (err) {
      toast.error('Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  if (!item) return null;

  const typeColor = TYPE_COLORS[item.item_type] || TYPE_COLORS.tool;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl text-gray-800 flex items-center gap-2">
            <Edit3 className="w-5 h-5 text-edupaws-sage" />
            Edit Item
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-5 pt-2">
          {/* Item Info Header */}
          <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
            <p className="font-body font-bold text-gray-800 text-base">{item.item_name}</p>
            <div className="flex items-center gap-2 mt-1">
              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${typeColor.bg} ${typeColor.text} border ${typeColor.border}`}>
                <span className={`w-1.5 h-1.5 rounded-full ${typeColor.dot}`}></span>
                {item.item_type}
              </span>
              <span className="text-xs text-gray-400">{item.box_title}</span>
            </div>
          </div>

          {/* Quantity */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-gray-700 font-body font-medium text-sm">Quantity on Hand</Label>
              <Input
                data-testid="edit-quantity"
                type="number"
                min="0"
                value={form.quantity_on_hand}
                onChange={(e) => setForm({ ...form, quantity_on_hand: parseInt(e.target.value) || 0 })}
                className="mt-1 h-11 bg-white border-gray-200 rounded-lg font-body"
              />
            </div>
            <div>
              <Label className="text-gray-700 font-body font-medium text-sm">Low Stock Alert At</Label>
              <Input
                data-testid="edit-threshold"
                type="number"
                min="0"
                value={form.low_stock_threshold}
                onChange={(e) => setForm({ ...form, low_stock_threshold: parseInt(e.target.value) || 0 })}
                className="mt-1 h-11 bg-white border-gray-200 rounded-lg font-body"
              />
            </div>
          </div>

          {/* Supplier Info */}
          <div className="border-t border-gray-100 pt-4">
            <p className="font-body font-bold text-gray-700 text-sm mb-3">Supplier Information</p>
            <div className="space-y-3">
              <div>
                <Label className="text-gray-600 font-body text-sm">Supplier Name</Label>
                <Input
                  data-testid="edit-supplier-name"
                  value={form.supplier_name}
                  onChange={(e) => setForm({ ...form, supplier_name: e.target.value })}
                  placeholder="e.g. Amazon, Chewy, Custom Vendor"
                  className="mt-1 h-11 bg-white border-gray-200 rounded-lg font-body"
                />
              </div>
              <div>
                <Label className="text-gray-600 font-body text-sm">Supplier URL</Label>
                <Input
                  data-testid="edit-supplier-url"
                  value={form.supplier_url}
                  onChange={(e) => setForm({ ...form, supplier_url: e.target.value })}
                  placeholder="https://supplier-website.com"
                  className="mt-1 h-11 bg-white border-gray-200 rounded-lg font-body"
                />
              </div>
            </div>
          </div>

          {/* Supplier Login Credentials */}
          <div className="border-t border-gray-100 pt-4">
            <p className="font-body font-bold text-gray-700 text-sm mb-3">Supplier Login Credentials</p>
            <div className="space-y-3">
              <div>
                <Label className="text-gray-600 font-body text-sm">Username / Email</Label>
                <Input
                  data-testid="edit-supplier-username"
                  value={form.supplier_username}
                  onChange={(e) => setForm({ ...form, supplier_username: e.target.value })}
                  placeholder="login@email.com"
                  className="mt-1 h-11 bg-white border-gray-200 rounded-lg font-body"
                />
              </div>
              <div>
                <Label className="text-gray-600 font-body text-sm">Password</Label>
                <div className="relative mt-1">
                  <Input
                    data-testid="edit-supplier-password"
                    type={showPassword ? 'text' : 'password'}
                    value={form.supplier_password}
                    onChange={(e) => setForm({ ...form, supplier_password: e.target.value })}
                    placeholder="Supplier account password"
                    className="h-11 bg-white border-gray-200 rounded-lg pr-11 font-body"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Notes */}
          <div className="border-t border-gray-100 pt-4">
            <Label className="text-gray-700 font-body font-medium text-sm">Notes</Label>
            <textarea
              data-testid="edit-notes"
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="Any additional notes about this item..."
              rows={3}
              className="mt-1 w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-body focus:outline-none focus:ring-2 focus:ring-edupaws-sage/30 focus:border-edupaws-sage resize-none"
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-2">
            <button
              onClick={onClose}
              className="px-5 py-2.5 rounded-lg text-sm font-body font-medium text-gray-500 hover:bg-gray-100 transition-colors"
            >
              Cancel
            </button>
            <button
              data-testid="save-item-btn"
              onClick={handleSave}
              disabled={saving}
              className="px-5 py-2.5 rounded-lg text-sm font-body font-bold text-white bg-edupaws-navy hover:bg-edupaws-navy/90 transition-colors disabled:opacity-50 flex items-center gap-2"
            >
              <Save className="w-4 h-4" />
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function AddItemModal({ open, onClose, onAdd, boxOptions }) {
  const [form, setForm] = useState({
    box_number: 1,
    item_name: '',
    item_type: 'tool',
    quantity_on_hand: 0,
    low_stock_threshold: 5,
  });
  const [saving, setSaving] = useState(false);

  const handleAdd = async () => {
    if (!form.item_name.trim()) {
      toast.error('Item name is required');
      return;
    }
    setSaving(true);
    try {
      const box = boxOptions.find(b => b.number === form.box_number);
      const res = await axios.post(`${API}/admin/inventory`, {
        ...form,
        box_title: box ? box.title : `Box ${form.box_number}`,
      });
      onAdd(res.data);
      toast.success(`Added ${form.item_name}`);
      setForm({ box_number: 1, item_name: '', item_type: 'tool', quantity_on_hand: 0, low_stock_threshold: 5 });
      onClose();
    } catch (err) {
      toast.error('Failed to add item');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl text-gray-800 flex items-center gap-2">
            <Plus className="w-5 h-5 text-edupaws-sage" />
            Add Inventory Item
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 pt-2">
          <div>
            <Label className="text-gray-700 font-body font-medium text-sm">Box</Label>
            <select
              data-testid="add-item-box"
              value={form.box_number}
              onChange={(e) => setForm({ ...form, box_number: parseInt(e.target.value) })}
              className="mt-1 w-full h-11 rounded-lg border border-gray-200 bg-white px-3 text-sm font-body focus:outline-none focus:ring-2 focus:ring-edupaws-sage/30"
            >
              {boxOptions.map(b => (
                <option key={b.number} value={b.number}>{b.title}</option>
              ))}
            </select>
          </div>
          <div>
            <Label className="text-gray-700 font-body font-medium text-sm">Item Name</Label>
            <Input
              data-testid="add-item-name"
              value={form.item_name}
              onChange={(e) => setForm({ ...form, item_name: e.target.value })}
              placeholder="e.g. Custom Training Card"
              className="mt-1 h-11 bg-white border-gray-200 rounded-lg font-body"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-gray-700 font-body font-medium text-sm">Type</Label>
              <select
                data-testid="add-item-type"
                value={form.item_type}
                onChange={(e) => setForm({ ...form, item_type: e.target.value })}
                className="mt-1 w-full h-11 rounded-lg border border-gray-200 bg-white px-3 text-sm font-body focus:outline-none focus:ring-2 focus:ring-edupaws-sage/30"
              >
                <option value="tool">Tool</option>
                <option value="toy">Toy</option>
                <option value="swag">Swag</option>
              </select>
            </div>
            <div>
              <Label className="text-gray-700 font-body font-medium text-sm">Initial Qty</Label>
              <Input
                data-testid="add-item-qty"
                type="number"
                min="0"
                value={form.quantity_on_hand}
                onChange={(e) => setForm({ ...form, quantity_on_hand: parseInt(e.target.value) || 0 })}
                className="mt-1 h-11 bg-white border-gray-200 rounded-lg font-body"
              />
            </div>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button onClick={onClose} className="px-5 py-2.5 rounded-lg text-sm font-body font-medium text-gray-500 hover:bg-gray-100">
              Cancel
            </button>
            <button
              data-testid="add-item-submit"
              onClick={handleAdd}
              disabled={saving}
              className="px-5 py-2.5 rounded-lg text-sm font-body font-bold text-white bg-edupaws-navy hover:bg-edupaws-navy/90 disabled:opacity-50 flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              {saving ? 'Adding...' : 'Add Item'}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function InventoryTab() {
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterStock, setFilterStock] = useState('all');
  const [expandedBoxes, setExpandedBoxes] = useState({});
  const [editItem, setEditItem] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);

  useEffect(() => { fetchInventory(); }, []);

  const fetchInventory = async () => {
    try {
      const [itemsRes, summaryRes] = await Promise.all([
        axios.get(`${API}/admin/inventory`),
        axios.get(`${API}/admin/inventory/summary`),
      ]);
      setItems(itemsRes.data);
      setSummary(summaryRes.data);
      // Expand all boxes by default
      const expanded = {};
      itemsRes.data.forEach(i => { expanded[i.box_number] = true; });
      setExpandedBoxes(expanded);
    } catch (err) {
      toast.error('Failed to load inventory');
    } finally {
      setLoading(false);
    }
  };

  const handleItemSaved = (updatedItem) => {
    setItems(prev => prev.map(i => i.id === updatedItem.id ? updatedItem : i));
    // Refresh summary
    axios.get(`${API}/admin/inventory/summary`).then(r => setSummary(r.data)).catch(() => {});
  };

  const handleItemAdded = (newItem) => {
    setItems(prev => [...prev, newItem]);
    axios.get(`${API}/admin/inventory/summary`).then(r => setSummary(r.data)).catch(() => {});
  };

  const handleDeleteItem = async (itemId, itemName) => {
    if (!window.confirm(`Delete "${itemName}" from inventory?`)) return;
    try {
      await axios.delete(`${API}/admin/inventory/${itemId}`);
      setItems(prev => prev.filter(i => i.id !== itemId));
      toast.success(`Deleted ${itemName}`);
      axios.get(`${API}/admin/inventory/summary`).then(r => setSummary(r.data)).catch(() => {});
    } catch (err) {
      toast.error('Failed to delete item');
    }
  };

  const toggleBox = (boxNum) => {
    setExpandedBoxes(prev => ({ ...prev, [boxNum]: !prev[boxNum] }));
  };

  // Filter and search
  const filteredItems = useMemo(() => {
    return items.filter(item => {
      const matchesSearch = !searchQuery ||
        item.item_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.supplier_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.box_title?.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesType = filterType === 'all' || item.item_type === filterType;
      let matchesStock = true;
      if (filterStock === 'low') matchesStock = item.quantity_on_hand <= item.low_stock_threshold && item.quantity_on_hand > 0;
      if (filterStock === 'out') matchesStock = item.quantity_on_hand === 0;
      if (filterStock === 'ok') matchesStock = item.quantity_on_hand > item.low_stock_threshold;
      return matchesSearch && matchesType && matchesStock;
    });
  }, [items, searchQuery, filterType, filterStock]);

  // Group by box
  const groupedByBox = useMemo(() => {
    const groups = {};
    filteredItems.forEach(item => {
      if (!groups[item.box_number]) {
        groups[item.box_number] = { title: item.box_title, items: [], phase: PHASE_MAP[item.box_number] || 'Foundation' };
      }
      groups[item.box_number].items.push(item);
    });
    return groups;
  }, [filteredItems]);

  const boxOptions = useMemo(() => {
    const seen = {};
    items.forEach(i => { if (!seen[i.box_number]) seen[i.box_number] = i.box_title; });
    return Object.entries(seen).map(([num, title]) => ({ number: parseInt(num), title })).sort((a, b) => a.number - b.number);
  }, [items]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[300px]">
        <div className="animate-spin rounded-full h-10 w-10 border-4 border-edupaws-sage border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div data-testid="inventory-tab" className="space-y-6">
      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div data-testid="stat-total-items" className="bg-white rounded-xl border border-gray-200 p-4">
            <p className="text-xs font-body font-medium text-gray-400 uppercase tracking-wider">Total Items</p>
            <p className="text-2xl font-heading font-bold text-gray-800 mt-1">{summary.total_items}</p>
          </div>
          <div data-testid="stat-total-units" className="bg-white rounded-xl border border-gray-200 p-4">
            <p className="text-xs font-body font-medium text-gray-400 uppercase tracking-wider">Total Units</p>
            <p className="text-2xl font-heading font-bold text-gray-800 mt-1">{summary.total_units}</p>
          </div>
          <div data-testid="stat-low-stock" className={`rounded-xl border p-4 ${summary.low_stock > 0 ? 'bg-amber-50 border-amber-200' : 'bg-white border-gray-200'}`}>
            <p className={`text-xs font-body font-medium uppercase tracking-wider ${summary.low_stock > 0 ? 'text-amber-600' : 'text-gray-400'}`}>Low Stock</p>
            <p className={`text-2xl font-heading font-bold mt-1 ${summary.low_stock > 0 ? 'text-amber-700' : 'text-gray-800'}`}>{summary.low_stock}</p>
          </div>
          <div data-testid="stat-out-of-stock" className={`rounded-xl border p-4 ${summary.out_of_stock > 0 ? 'bg-red-50 border-red-200' : 'bg-white border-gray-200'}`}>
            <p className={`text-xs font-body font-medium uppercase tracking-wider ${summary.out_of_stock > 0 ? 'text-red-600' : 'text-gray-400'}`}>Out of Stock</p>
            <p className={`text-2xl font-heading font-bold mt-1 ${summary.out_of_stock > 0 ? 'text-red-700' : 'text-gray-800'}`}>{summary.out_of_stock}</p>
          </div>
        </div>
      )}

      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
        <div className="flex flex-1 gap-3 items-center w-full sm:w-auto">
          <div className="relative flex-1 sm:max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              data-testid="inventory-search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search items, suppliers..."
              className="pl-10 h-10 bg-white border-gray-200 rounded-lg font-body text-sm"
            />
          </div>
          <select
            data-testid="filter-type"
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="h-10 rounded-lg border border-gray-200 bg-white px-3 text-sm font-body focus:outline-none focus:ring-2 focus:ring-edupaws-sage/30"
          >
            <option value="all">All Types</option>
            <option value="tool">Tools</option>
            <option value="toy">Toys</option>
            <option value="swag">Swag</option>
          </select>
          <select
            data-testid="filter-stock"
            value={filterStock}
            onChange={(e) => setFilterStock(e.target.value)}
            className="h-10 rounded-lg border border-gray-200 bg-white px-3 text-sm font-body focus:outline-none focus:ring-2 focus:ring-edupaws-sage/30"
          >
            <option value="all">All Stock</option>
            <option value="low">Low Stock</option>
            <option value="out">Out of Stock</option>
            <option value="ok">In Stock</option>
          </select>
        </div>
        <button
          data-testid="add-item-btn"
          onClick={() => setShowAddModal(true)}
          className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-body font-bold text-white bg-edupaws-sage hover:bg-edupaws-sage/90 transition-colors shrink-0"
        >
          <Plus className="w-4 h-4" />
          Add Item
        </button>
      </div>

      {/* Inventory Grid by Box */}
      <div className="space-y-4">
        {Object.entries(groupedByBox)
          .sort(([a], [b]) => parseInt(a) - parseInt(b))
          .map(([boxNum, group]) => {
            const phase = group.phase;
            const phaseColor = PHASE_COLORS[phase] || PHASE_COLORS.Foundation;
            const isExpanded = expandedBoxes[parseInt(boxNum)];
            const boxLowStock = group.items.filter(i => i.quantity_on_hand <= i.low_stock_threshold).length;

            return (
              <div key={boxNum} data-testid={`box-group-${boxNum}`} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                {/* Box Header */}
                <button
                  data-testid={`box-toggle-${boxNum}`}
                  onClick={() => toggleBox(parseInt(boxNum))}
                  className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${phaseColor} flex items-center justify-center`}>
                      <span className="text-white font-heading font-bold text-sm">{boxNum}</span>
                    </div>
                    <div className="text-left">
                      <p className="font-body font-bold text-gray-800 text-sm">{group.title}</p>
                      <p className="text-xs text-gray-400 font-body">{phase} &middot; {group.items.length} items</p>
                    </div>
                    {boxLowStock > 0 && (
                      <span className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold bg-amber-100 text-amber-700">
                        <AlertTriangle className="w-3 h-3" />
                        {boxLowStock} low
                      </span>
                    )}
                  </div>
                  {isExpanded ? <ChevronDown className="w-5 h-5 text-gray-400" /> : <ChevronRight className="w-5 h-5 text-gray-400" />}
                </button>

                {/* Items Table */}
                {isExpanded && (
                  <div className="border-t border-gray-100">
                    <table className="w-full">
                      <thead>
                        <tr className="bg-gray-50/50">
                          <th className="text-left px-4 py-2.5 text-xs font-body font-bold text-gray-500 uppercase tracking-wider">Item</th>
                          <th className="text-left px-4 py-2.5 text-xs font-body font-bold text-gray-500 uppercase tracking-wider">Type</th>
                          <th className="text-center px-4 py-2.5 text-xs font-body font-bold text-gray-500 uppercase tracking-wider">Qty</th>
                          <th className="text-left px-4 py-2.5 text-xs font-body font-bold text-gray-500 uppercase tracking-wider">Status</th>
                          <th className="text-left px-4 py-2.5 text-xs font-body font-bold text-gray-500 uppercase tracking-wider">Supplier</th>
                          <th className="text-right px-4 py-2.5 text-xs font-body font-bold text-gray-500 uppercase tracking-wider">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-50">
                        {group.items.map(item => {
                          const tc = TYPE_COLORS[item.item_type] || TYPE_COLORS.tool;
                          return (
                            <tr key={item.id} data-testid={`inventory-row-${item.id}`} className="hover:bg-gray-50/50 transition-colors">
                              <td className="px-4 py-3">
                                <p className="font-body font-medium text-gray-800 text-sm">{item.item_name}</p>
                                {item.notes && <p className="text-xs text-gray-400 font-body mt-0.5 truncate max-w-[200px]">{item.notes}</p>}
                              </td>
                              <td className="px-4 py-3">
                                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${tc.bg} ${tc.text} border ${tc.border}`}>
                                  <span className={`w-1.5 h-1.5 rounded-full ${tc.dot}`}></span>
                                  {item.item_type}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-center">
                                <span className={`font-body font-bold text-sm ${item.quantity_on_hand === 0 ? 'text-red-600' : item.quantity_on_hand <= item.low_stock_threshold ? 'text-amber-600' : 'text-gray-800'}`}>
                                  {item.quantity_on_hand}
                                </span>
                              </td>
                              <td className="px-4 py-3">
                                <StockBadge quantity={item.quantity_on_hand} threshold={item.low_stock_threshold} />
                              </td>
                              <td className="px-4 py-3">
                                {item.supplier_name ? (
                                  <div className="flex items-center gap-1.5">
                                    <span className="font-body text-sm text-gray-600 truncate max-w-[120px]">{item.supplier_name}</span>
                                    {item.supplier_url && (
                                      <a href={item.supplier_url} target="_blank" rel="noopener noreferrer" className="text-edupaws-sage hover:text-edupaws-navy">
                                        <ExternalLink className="w-3.5 h-3.5" />
                                      </a>
                                    )}
                                  </div>
                                ) : (
                                  <span className="text-xs text-gray-300 font-body italic">No supplier</span>
                                )}
                              </td>
                              <td className="px-4 py-3 text-right">
                                <div className="flex items-center justify-end gap-1">
                                  <button
                                    data-testid={`edit-btn-${item.id}`}
                                    onClick={() => setEditItem(item)}
                                    className="p-1.5 rounded-lg text-gray-400 hover:text-edupaws-navy hover:bg-gray-100 transition-colors"
                                    title="Edit item"
                                  >
                                    <Edit3 className="w-4 h-4" />
                                  </button>
                                  <button
                                    data-testid={`delete-btn-${item.id}`}
                                    onClick={() => handleDeleteItem(item.id, item.item_name)}
                                    className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                                    title="Delete item"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })}

        {Object.keys(groupedByBox).length === 0 && (
          <div className="text-center py-16 bg-white rounded-xl border border-gray-200">
            <Package className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="font-body font-bold text-gray-400">No items match your filters</p>
            <p className="font-body text-sm text-gray-300 mt-1">Try adjusting your search or filters</p>
          </div>
        )}
      </div>

      {/* Edit Modal */}
      <ItemEditModal
        item={editItem}
        open={!!editItem}
        onClose={() => setEditItem(null)}
        onSave={handleItemSaved}
      />

      {/* Add Item Modal */}
      <AddItemModal
        open={showAddModal}
        onClose={() => setShowAddModal(false)}
        onAdd={handleItemAdded}
        boxOptions={boxOptions}
      />
    </div>
  );
}
